import React, { memo, useEffect } from 'react';
import { Box, Divider, Hidden, HStack, NativeBaseProvider, VStack } from 'native-base';
import { FullAppTemplate } from '../../templates';
import { Borrower, Declarations, ProgressSteps, PropertyInformation } from '../../organisms';
import { BackwardArrowIcon, Button, ForwardArrowIcon, Typography } from '../../atoms';
import { useFullAppActions, useFullAppStore, useIsInEditMode, useNextDisabled, useSelectTemplate, useSelectTemplatePrevious, useTemplateInfoByScreen } from '../../../reducers';
import { ReturnToHome } from '../../molecules';
import { FullAppContextProvider, useFullAppContext } from '../../../context';
import { fullAppTheme, FullAppTranslations } from '../../../../shared';
import translation from '../../../../shared/helpers/Translation';
import { Demographic } from '../../organisms/Demographic';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { isEmpty, startCase } from 'lodash';
import { usePlatformScrollToTop } from '../../../hooks';
const Client = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false
    }
  }
});
const ScreenToComponent = {
  borrowers: /*#__PURE__*/React.createElement(Borrower, null),
  additional: /*#__PURE__*/React.createElement(Borrower, null),
  property: /*#__PURE__*/React.createElement(PropertyInformation, null),
  declarations: /*#__PURE__*/React.createElement(Declarations, null),
  demographic: /*#__PURE__*/React.createElement(Demographic, null)
};
translation.setTranslations({
  ...FullAppTranslations
});
const FormWrapper = _ref => {
  let {
    onReturnClicked,
    setReturnToHome
  } = _ref;
  const {
    steps,
    title,
    subtitle,
    disclaimer,
    informationsubtitle
  } = useTemplateInfoByScreen();
  const {
    setNextScreen,
    setPreviousScreen,
    submit
  } = useFullAppActions();
  const previous = useSelectTemplatePrevious();
  const isDisabled = useNextDisabled();
  const {
    isEditing
  } = useIsInEditMode();
  const {
    screen
  } = useSelectTemplate();
  const {
    logUserEvent,
    productCode,
    loanType,
    scrollRef,
    logApiEvent
  } = useFullAppContext();
  const isLoading = useFullAppStore(state => state.isLoading);
  usePlatformScrollToTop({
    screen,
    scrollRef,
    productCode,
    loanType,
    logUserEvent
  });
  useEffect(() => {
    setReturnToHome(previous > 0);
  }, [previous, setReturnToHome]);
  return /*#__PURE__*/React.createElement(FullAppTemplate, null, /*#__PURE__*/React.createElement(FullAppTemplate.Header, {
    key: 'header-section'
  }, !previous ? null : /*#__PURE__*/React.createElement(Hidden, {
    from: "lg"
  }, /*#__PURE__*/React.createElement(Box, {
    flex: "1",
    mb: "8",
    alignSelf: "flex-end"
  }, /*#__PURE__*/React.createElement(ReturnToHome, {
    onPress: onReturnClicked
  }))), /*#__PURE__*/React.createElement(ProgressSteps, {
    steps: steps
  }), /*#__PURE__*/React.createElement(VStack, {
    space: 4,
    mt: 8,
    key: 'title-section'
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "h1",
    flexWrap: "wrap",
    key: title,
    accessibilityLabel: title,
    testID: "title"
  }, title), !isEmpty(informationsubtitle) ? /*#__PURE__*/React.createElement(Typography, {
    variant: "body16",
    key: informationsubtitle,
    accessibilityLabel: informationsubtitle,
    testID: "subtitle1"
  }, informationsubtitle) : null, /*#__PURE__*/React.createElement(Typography, {
    variant: "body16",
    key: subtitle,
    accessibilityLabel: subtitle,
    testID: "subtitle"
  }, subtitle))), /*#__PURE__*/React.createElement(FullAppTemplate.Main, {
    key: 'main-section'
  }, ScreenToComponent[screen]), /*#__PURE__*/React.createElement(FullAppTemplate.Footer, {
    key: 'footer-section'
  }, /*#__PURE__*/React.createElement(VStack, {
    space: 4
  }, /*#__PURE__*/React.createElement(Divider, {
    orientation: "horizontal",
    color: "darkGray.200"
  }), /*#__PURE__*/React.createElement(Typography, {
    variant: "subtitle",
    color: "#212121",
    accessibilityLabel: disclaimer,
    testID: "disclaimer"
  }, disclaimer), !previous ? /*#__PURE__*/React.createElement(Button, {
    accessibilityLabel: "next",
    testID: "next_button",
    alignSelf: "flex-end",
    onPress: () => {
      logUserEvent({
        action: `${startCase(screen)}InfoNextClicked`,
        type: 'DigitalApp',
        description: `Product Code - ${productCode}, Loan Type - ${loanType}`
      });
      setNextScreen(logApiEvent);
    },
    isDisabled: isDisabled,
    key: 'first-screen-nxt-button',
    rightIcon: /*#__PURE__*/React.createElement(ForwardArrowIcon, null)
  }, "Next") : /*#__PURE__*/React.createElement(HStack, {
    justifyContent: "space-between"
  }, /*#__PURE__*/React.createElement(Button, {
    accessibilityLabel: "back",
    testID: "back_button",
    onPress: () => {
      logUserEvent({
        action: `${startCase(screen)}InfoBackClicked`,
        type: 'DigitalApp',
        description: `Product Code - ${productCode}, Loan Type - ${loanType}`
      });
      setPreviousScreen();
    },
    isDisabled: isEditing,
    leftIcon: /*#__PURE__*/React.createElement(BackwardArrowIcon, null),
    variant: 'ghost'
  }, "Back"), /*#__PURE__*/React.createElement(Button, {
    accessibilityLabel: screen === 'demographic' ? 'submit' : 'next',
    testID: screen === 'demographic' ? 'submit_button' : 'next_button',
    alignSelf: "flex-end",
    onPress: () => {
      if (screen === 'demographic') {
        logUserEvent({
          action: `${startCase(screen)}SubmitClicked`,
          type: 'DigitalApp',
          description: `Product Code - ${productCode}, Loan Type - ${loanType}`
        });
        submit(logApiEvent);
      } else {
        logUserEvent({
          action: `${startCase(screen)}InfoNextClicked`,
          type: 'DigitalApp',
          description: `Product Code - ${productCode}, Loan Type - ${loanType}`
        });
        setNextScreen(logApiEvent);
      }
    },
    key: 'nxt-button',
    variant: 'solid',
    rightIcon: /*#__PURE__*/React.createElement(ForwardArrowIcon, null),
    isDisabled: isDisabled,
    isLoading: isLoading,
    spinnerPlacement: "end"
  }, screen === 'demographic' ? 'Submit' : 'Next')))));
};
export default /*#__PURE__*/memo(props => {
  return /*#__PURE__*/React.createElement(NativeBaseProvider, {
    theme: fullAppTheme
  }, /*#__PURE__*/React.createElement(QueryClientProvider, {
    client: Client
  }, /*#__PURE__*/React.createElement(FullAppContextProvider, props, /*#__PURE__*/React.createElement(FormWrapper, props))));
});
//# sourceMappingURL=CRTForm.js.map